import { useOperator } from "./useOperator";
import { useFeatureFlag } from "./useFeatureFlag";

export const VEIVO_FEATURE_FLAG = "ff-vehicle-veivo-validation";

/**
 * Which add-vehicle flow this operator gets: KYV (VIN + Veivo) or the legacy
 * plate lookup.
 *
 * Country first, then the flag.
 *
 * OUTSIDE AUSTRALIA the flag is not consulted. The legacy front door looks a
 * plate up in a vehicle registry, and Australia is the only country we have one
 * for, so KYV is not a preference elsewhere — it is the only thing that works.
 * A flag able to answer "off" for an NZ or US operator would leave them with no
 * way to add a vehicle at all.
 *
 * IN AUSTRALIA both flows work, so `ff-vehicle-veivo-validation` decides. It
 * defaults off: an AU operator keeps the plate flow until someone opts them in.
 *
 * This mirrors `VeivoFacade.isEnabledForProvider` in web-app-v2, which is the
 * ENFORCEMENT point — it refuses to start a validation for an operator who
 * should not have one. This hook only decides what to render. Keep the two
 * rules in step; the server is the one that matters.
 */
export const useKyvEnabled = (): { enabled: boolean; isLoading: boolean } => {
  const { country } = useOperator();
  const { enabled: flagEnabled, isLoading } = useFeatureFlag(VEIVO_FEATURE_FLAG);

  const isAustralia = (country ?? "").toUpperCase() === "AU";

  // No flag to wait for outside AU, so don't report loading either — a caller
  // that blocks render on isLoading would otherwise stall on a question that
  // has already been answered.
  if (!isAustralia) return { enabled: true, isLoading: false };

  return { enabled: flagEnabled, isLoading };
};
